import React, { useState } from "react";
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Button,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { IComponenteCor, ISelectOption } from "../types/componente.types";

// Estilos padronizados para inputs (igual à aba GERAL)
const inputSx = {
  backgroundColor: "#f0f0f0",
  borderRadius: 0,
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiInputBase-input": {
    padding: "20px 14px 8px 14px",
  },
  minHeight: 56,
};

const selectSx = {
  backgroundColor: "#f0f0f0",
  borderRadius: 0,
  minHeight: 56,
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiSelect-select": {
    padding: "20px 14px 8px 14px",
  },
};

const labelSx = {
  color: "#3C90AD",
  fontWeight: 500,
  fontSize: "0.8rem",
  "&.Mui-focused": {
    color: "#3C90AD",
  },
};

interface TabCoresProps {
  cores: IComponenteCor[];
  onChange: (cores: IComponenteCor[]) => void;
  coresLista: ISelectOption[];
  codigoBase: string;
}

export const TabCores: React.FC<TabCoresProps> = ({
  cores,
  onChange,
  coresLista,
  codigoBase,
}) => {
  const [itemParaRemover, setItemParaRemover] = useState<number | null>(null);

  // Verificar duplicatas de cor
  const verificarCorDuplicada = (idCor: number, ordem?: number): boolean => {
    return cores.some(
      (c) => c.idCor === idCor && c.ordem !== ordem
    );
  };

  // Verificar duplicatas de código do produto
  const verificarCodigoDuplicado = (codigoProduto: string, ordem?: number): boolean => {
    if (!codigoProduto) return false;
    return cores.some(
      (c) => c.codigoProduto === codigoProduto && c.ordem !== ordem
    );
  };

  const handleAdicionar = () => {
    const novaOrdem = cores.length > 0
      ? Math.max(...cores.map((c) => c.ordem || 0)) + 1
      : 1;

    onChange([
      {
        ordem: novaOrdem,
        idCor: 0,
        corDescricao: "",
        codigoProduto: "",
      },
      ...cores, // Novo item no topo
    ]);
  };

  const handleAlterarCampo = (
    ordem: number,
    campo: keyof IComponenteCor,
    valor: any
  ) => {
    onChange(
      cores.map((c) =>
        c.ordem === ordem ? { ...c, [campo]: valor } : c
      )
    );
  };

  const handleRemover = (ordem: number) => {
    onChange(cores.filter((c) => c.ordem !== ordem));
    setItemParaRemover(null);
  };

  // Reordenar cores após remoção
  const coresReordenadas = cores.map((c, index) => ({
    ...c,
    ordem: index + 1,
  }));

  // Atualizar ordem se necessário
  React.useEffect(() => {
    if (coresReordenadas.some((c, i) => c.ordem !== i + 1)) {
      onChange(coresReordenadas);
    }
  }, [cores.length]);

  return (
    <Box>
      {/* Cabeçalho com botão ADICIONAR COR + */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ color: "#3C90AD", fontWeight: 600 }}>
          Cores do Componente
        </Typography>
        <Tooltip title="Ao clicar, uma nova linha será inserida para preenchimento.">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdicionar}
            sx={{
              backgroundColor: "#3C90AD",
              "&:hover": { backgroundColor: "#2d6f87" },
            }}
          >
            ADICIONAR COR +
          </Button>
        </Tooltip>
      </Box>

      {/* Lista de Cores */}
      {cores.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center", backgroundColor: "#fafafa" }}>
          <Typography variant="body2" color="text.secondary">
            Nenhuma cor adicionada.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {cores.map((cor) => {
            const isCorDuplicada = cor.idCor > 0 && verificarCorDuplicada(cor.idCor, cor.ordem);
            const isCodigoDuplicado = !!(
              cor.codigoProduto &&
              verificarCodigoDuplicado(cor.codigoProduto, cor.ordem)
            );
            const corSelecionada = coresLista.find((c) => c.id === cor.idCor);

            return (
              <Paper
                key={cor.ordem}
                sx={{
                  p: 2,
                  backgroundColor: "#f5f5f5",
                  border: "1px solid #e0e0e0",
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  {/* Coluna Ordem */}
                  <Grid item xs={1}>
                    <Typography
                      variant="body2"
                      sx={{ color: "#666", fontWeight: 500, textAlign: "center" }}
                    >
                      {cor.ordem}
                    </Typography>
                  </Grid>

                  {/* Coluna Cor */}
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth>
                      <InputLabel sx={labelSx} required>
                        Cor
                      </InputLabel>
                      <Select
                        value={cor.idCor || ""}
                        label="Cor"
                        onChange={(e) => {
                          // FASE 05 — 2ª validação/PO 2026-07-24 (D7): atualizar idCor e
                          // corDescricao numa ÚNICA atualização de estado. Duas chamadas
                          // seguidas partiam do mesmo array (closure), e a 2ª sobrescrevia
                          // a 1ª — a cor selecionada não aparecia no campo.
                          const idCor = e.target.value as number;
                          const corSelecionada = coresLista.find((c) => c.id === idCor);
                          onChange(
                            cores.map((c) =>
                              c.ordem === cor.ordem
                                ? { ...c, idCor, corDescricao: corSelecionada?.nome || "" }
                                : c
                            )
                          );
                        }}
                        required
                        error={isCorDuplicada}
                        sx={selectSx}
                      >
                        <MenuItem value="">
                          <em>Selecione...</em>
                        </MenuItem>
                        {coresLista.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.nome || c.descricao}
                          </MenuItem>
                        ))}
                      </Select>
                      {isCorDuplicada && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                          Esta cor já foi adicionada
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Coluna Código do produto */}
                  <Grid item xs={12} sm={5}>
                    <Tooltip title="Se informado, este código representa o produto para a cor selecionada.">
                      <TextField
                        fullWidth
                        label="Código do produto"
                        value={cor.codigoProduto || ""}
                        onChange={(e) =>
                          handleAlterarCampo(
                            cor.ordem,
                            "codigoProduto",
                            e.target.value.toUpperCase()
                          )
                        }
                        placeholder={`Ex: ${codigoBase}BCO, ${codigoBase}PTO`}
                        error={isCodigoDuplicado}
                        helperText={
                          isCodigoDuplicado
                            ? "Este código de produto já foi usado"
                            : corSelecionada && codigoBase
                            ? `Exibição: ${codigoBase} – ${corSelecionada.nome}`
                            : ""
                        }
                        InputLabelProps={{ sx: labelSx }}
                        sx={inputSx}
                      />
                    </Tooltip>
                  </Grid>

                  {/* Coluna Ações */}
                  <Grid item xs={12} sm={2}>
                    <IconButton
                      onClick={() => setItemParaRemover(cor.ordem)}
                      sx={{ color: "#f44336" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* Modal de Confirmação de Exclusão */}
      <Dialog open={itemParaRemover !== null} onClose={() => setItemParaRemover(null)}>
        <DialogTitle>Remover cor</DialogTitle>
        <DialogContent>
          <Typography>Tem certeza que deseja remover esta cor?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemParaRemover(null)}>Cancelar</Button>
          <Button
            onClick={() => itemParaRemover !== null && handleRemover(itemParaRemover)}
            color="error"
            variant="contained"
          >
            Remover
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
